import { z } from 'zod';
import { GlobalSearchConfidenceFilter, GlobalSearchTimeFilter, GlobalSearchTypeFilter, } from './global-search.js';
/**
 * Rail-side filter vocabulary for the `/search` page's `<SearchFilterRail>`.
 *
 * Two of the four facets — `type` and `confidence` — share their vocabulary
 * 1:1 with the wire (`Global*Filter` in `./global-search.ts`), so they're
 * re-exported as `SearchTypeFilter` / `SearchConfidenceFilter` aliases. The
 * other two diverge for UI reasons:
 *
 * - `SearchTimeFilter` uses concise tokens (`'7d'`, `'30d'`) that match the
 *   i18n keys (`search.filter.time.7d`); the wire uses `'last-7d'` /
 *   `'last-30d'`. {@link railTimeToWire} converts.
 * - `SearchSourceFilter` is a closed allow-list of connector ids the UI can
 *   render; the wire's `source` field is an open `z.string().max(64)` so
 *   future connectors don't require a wire bump.
 *
 * Closed-string-set rule (CLAUDE.md): every value drawn from a finite,
 * known set lives as a `z.enum` here in `packages/shared` and is referenced
 * via `Filter.enum.foo` at every call site. The `DEFAULT_SEARCH_FILTERS`
 * sentinel is exported from this same module so the page + router parse
 * agree on "show all" semantics.
 *
 * Previously these enums lived in `apps/web/src/components/search/search-
 * filter-rail.tsx`. They were promoted to shared as part of PR #142 review
 * so the router's `validateSearch` for `/search` can enforce the enum
 * vocabulary at the URL boundary instead of accepting arbitrary strings.
 */
/**
 * Type facet — re-export of the wire enum. The rail previously used a
 * cosmetic `'everything'` sentinel that didn't exist as an i18n key
 * (`search.filter.type.all` was the actual label); unifying on the wire
 * vocabulary fixes that missing-key bug.
 */
export const SearchTypeFilter = GlobalSearchTypeFilter;
/**
 * Source facet — closed UI-side allow-list of connector ids the rail
 * renders. Stays distinct from the wire's open-typed `source: string` so
 * adding a new option here surfaces a TypeScript error at the i18n call
 * site before it can be rendered with a missing label.
 */
export const SearchSourceFilter = z.enum([
    'all',
    'salesforce',
    'slack',
    'gmail',
    'notion',
    'drive',
    'github',
]);
/**
 * Time facet — rail vocabulary (`'7d'` / `'30d'` instead of the wire's
 * `'last-7d'` / `'last-30d'`). Kept separate because the i18n keys
 * (`search.filter.time.7d`) match this vocabulary; renaming would
 * cascade into i18n + the designer's mock labels.
 */
export const SearchTimeFilter = z.enum(['today', '7d', '30d', 'quarter', 'year', 'all']);
/**
 * Confidence facet — re-export of the wire enum. Vocabularies match 1:1
 * (`'all'` / `'high'` / `'medium'` / `'low'`).
 */
export const SearchConfidenceFilter = GlobalSearchConfidenceFilter;
/** Sensible defaults — "show everything". Exported so the page, the rail,
 *  and the router's `validateSearch` all share one source of truth. */
export const DEFAULT_SEARCH_FILTERS = {
    type: SearchTypeFilter.enum.all,
    source: SearchSourceFilter.enum.all,
    time: SearchTimeFilter.enum.all,
    confidence: SearchConfidenceFilter.enum.all,
};
/**
 * URL search-params schema for the `/search` route. Each facet is gated
 * to its enum so the router's `validateSearch` rejects malformed values
 * at the URL boundary instead of letting them through to the page where
 * they'd get silently fall-through-to-default'd.
 *
 * `q` is bounded to match the `GlobalSearchRequest.query` cap; `source`
 * is optional because the rail's `'all'` sentinel is omitted from the
 * URL (see `filtersToUrlBag` in `apps/web/src/pages/search.tsx`).
 */
export const SearchPageSearchSchema = z.object({
    q: z.string().min(1).max(2000).optional(),
    type: SearchTypeFilter.optional(),
    time: SearchTimeFilter.optional(),
    confidence: SearchConfidenceFilter.optional(),
    source: SearchSourceFilter.optional(),
});
/**
 * Rail-vocabulary → wire-vocabulary translation maps. Lifted to shared
 * so the page + any future caller (server-side rendering, deep-link
 * tooling) round-trip through the same conversion.
 */
export function railTimeToWire(time) {
    switch (time) {
        case '7d':
            return GlobalSearchTimeFilter.enum['last-7d'];
        case '30d':
            return GlobalSearchTimeFilter.enum['last-30d'];
        default:
            // The other tokens (`today` / `quarter` / `year` / `all`) match 1:1.
            return time;
    }
}
